import { trpc, trpcRetry } from "@carrot/bff-client"
import {
  APPOINTMENTS_QUERY_KEY,
  APPOINTMENTS_SHARED_STALE_MS,
  hasEnded
} from "@internal/constants/telemedicine/appointments"
import { JOIN_LINK_SHARED_STALE_MS, joinLinkQueryKey } from "@internal/constants/telemedicine/join-link"
import { useNow } from "@internal/ui"
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useTelemedicineAccess } from "../booking/hooks"
import type { AppointmentsPageData, PastAppointment, Provider, UpcomingAppointment } from "./types"

export type AppointmentsResult = {
  data: AppointmentsPageData | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * A React Query *mutation* result, narrowed to what the cancel dialog reads. `mutate` is
 * fire-and-forget with a success callback rather than awaitable, so a failed cancel cannot be
 * mistaken for a successful one — see `useCancelAppointment`.
 */
type CancelAppointmentResult = {
  mutate: (appointmentId: string, options: { cancellationAttemptId: string; onSuccess?: () => void }) => void
  isLoading: boolean
  isError: boolean
}

// A client-only overlay of appointments whose intake forms were just submitted, held in the query
// cache so it survives appointments refetches. Keyed by appointment id (the intake URL's id) and OR'd
// into `formsComplete`, so completion shows immediately — before Healthie's webhook reaches
// scheduling-api. Each entry is held until the server confirms completion (then handed off) or until
// FORMS_COMPLETE_MAX_MS elapses; see reconcileFormsCompleteOverlay. So it never claims completion indefinitely.
const FORMS_COMPLETE_OVERLAY_KEY = "telemedicine.formsCompleteOverlay"
type FormsCompleteOverlayEntry = { id: string; since: number }

// Stop optimistically showing completion after this long unconfirmed — a submission that never
// registered self-corrects to the CTA rather than claiming completion forever.
const FORMS_COMPLETE_MAX_MS = 120_000

/**
 * The `telemedicine.getAppointments` answer, derived from the client so it cannot drift from the
 * server contract. Exported for the test fixtures that stand in for that answer — an untyped literal
 * lets `memberTelemedicineAppointmentsSchema` rename a field and leave every test green while the
 * page renders nothing. See `test-utils/wire-fixtures.ts`.
 */
export type GetAppointmentsResult = Awaited<ReturnType<typeof trpc["telemedicine"]["getAppointments"]["query"]>>
type ApiAppointment = GetAppointmentsResult["upcoming"][number]
type ApiProvider = GetAppointmentsResult["specialists"][number]

const toProvider = (provider: ApiProvider): Provider => ({
  name: provider.name,
  title: provider.credential ?? undefined,
  photoUrl: provider.photoUrl ?? null
})

// `formsComplete` reads the just-submitted overlay as well as the record, so completion shows before
// the webhook lands (see FORMS_COMPLETE_OVERLAY_KEY).
const toUpcoming = (appointment: ApiAppointment, formsCompleteOverlay: ReadonlySet<string>): UpcomingAppointment => ({
  id: appointment.id,
  provider: toProvider(appointment.provider),
  appointmentType: appointment.appointmentType,
  date: new Date(appointment.startTime),
  formsComplete: appointment.formsComplete || formsCompleteOverlay.has(appointment.id),
  status: "scheduled"
})

const toPast = (appointment: ApiAppointment): PastAppointment => ({
  id: appointment.id,
  provider: toProvider(appointment.provider),
  appointmentType: appointment.appointmentType,
  date: appointment.startTime.slice(0, 10),
  status: "completed"
})

// Reactive read of the just-submitted overlay. Cache-only (never fetched): seeded by
// markAppointmentFormsComplete and pruned by reconcileFormsCompleteOverlay, with initialData so it's
// an empty list until then.
function useFormsCompleteOverlay(): readonly FormsCompleteOverlayEntry[] {
  const { data } = useQuery({
    queryKey: [FORMS_COMPLETE_OVERLAY_KEY],
    queryFn: () => [] as FormsCompleteOverlayEntry[],
    staleTime: Infinity,
    initialData: [] as FormsCompleteOverlayEntry[]
  })
  return data
}

// Whether this appointment's forms were marked complete this session, before the completion webhook
// reaches scheduling-api — lets the intake page avoid re-serving a form the member just finished.
export function useAppointmentFormsMarkedComplete(appointmentId: string): boolean {
  return useFormsCompleteOverlay().some((entry) => entry.id === appointmentId)
}

// On each appointments fetch, drop overlay entries the server now confirms complete (clean handoff to
// server truth) or that have outlived FORMS_COMPLETE_MAX_MS (self-correct). Returns the same array
// reference when nothing changes, so an unrelated fetch doesn't churn the overlay observer.
function reconcileFormsCompleteOverlay(queryClient: QueryClient, result: GetAppointmentsResult): void {
  const now = Date.now()
  queryClient.setQueryData<FormsCompleteOverlayEntry[]>([FORMS_COMPLETE_OVERLAY_KEY], (entries) => {
    if (!entries || entries.length === 0) return entries
    const next = entries.filter((entry) => {
      const confirmed = result.upcoming.some((appt) => appt.id === entry.id && appt.formsComplete === true)
      const expired = now - entry.since >= FORMS_COMPLETE_MAX_MS
      return !confirmed && !expired
    })
    return next.length === entries.length ? entries : next
  })
}

export function useAppointments(): AppointmentsResult {
  const queryClient = useQueryClient()
  const access = useTelemedicineAccess()
  const overlayEntries = useFormsCompleteOverlay()
  const overlayIds = new Set(overlayEntries.map((entry) => entry.id))
  const appointments = useQuery({
    queryKey: APPOINTMENTS_QUERY_KEY,
    queryFn: () => trpc.telemedicine.getAppointments.query(),
    // ⚠ SHARED WITH `@internal/consumer`'s plan card — same key, same staleness, one request. Focus
    // is the only refetch trigger; it covers what the clock below cannot, namely a visit cancelled
    // or booked from another device.
    refetchOnWindowFocus: true,
    staleTime: APPOINTMENTS_SHARED_STALE_MS,
    // Each fetch (mount or the refetch markAppointmentFormsComplete triggers) reconciles the overlay,
    // dropping entries the server now confirms complete or that have expired.
    onSuccess: (result) => reconcileFormsCompleteOverlay(queryClient, result),
    retry: trpcRetry(1)
  })

  // A finished visit leaves the list on the CLOCK, not a refetch: the BFF drops one that had ended
  // when it answered, but this surface outlives the answer. `at-the-crossing-only` because the
  // rail's countdown (`useAppointmentTiming`) owns the moving reading.
  const serverUpcoming = appointments.data?.upcoming ?? []
  const nextEnding = serverUpcoming.reduce<string | undefined>(
    (soonest, appointment) =>
      soonest === undefined || Date.parse(appointment.endTime) < Date.parse(soonest) ? appointment.endTime : soonest,
    undefined
  )
  const now = useNow(nextEnding === undefined ? null : new Date(nextEnding), "at-the-crossing-only")

  const isError = access.isError || appointments.isError
  if (isError || !access.data || !appointments.data) {
    return { data: undefined, isLoading: !isError, isError }
  }

  return {
    data: {
      eligible: access.data.programs.some((program) => program.qualified),
      upcoming: serverUpcoming
        .filter((appointment) => !hasEnded(appointment.endTime, now))
        .map((appointment) => toUpcoming(appointment, overlayIds)),
      past: appointments.data.past.map(toPast),
      specialists: appointments.data.specialists.map(toProvider)
    },
    isLoading: false,
    isError: false
  }
}

/**
 * The just-in-time visit join link, enabled only when the caller says the visit is imminent:
 * `connection.joinUrl` is transient PHI absent from the appointment list. Returns null when Healthie
 * has not provisioned a link.
 *
 * ⚠ THE KEY AND THE STALENESS WINDOW ARE SHARED WITH `@internal/consumer`'s OWN `useJoinLink`, off
 * `joinLinkQueryKey` — see that module. The plan card and this package's `HomeAppointmentsTile` are
 * siblings on the consumer home, both go live at the same imminence crossing and both call this
 * procedure with the same appointment id, and there is one `QueryClientProvider` in the app: under
 * two keys they made two reads of the same PHI for one visit. `staleTime` is what makes the sharing
 * real rather than nominal (a sibling enabling a beat later counts as stale and fetches again);
 * `cacheTime: 0` is untouched, so the entry still goes the moment neither surface is observing it.
 */
export function useJoinLink(appointmentId: string, options: { enabled: boolean }): { joinUrl: string | null } {
  const query = useQuery({
    queryKey: joinLinkQueryKey(appointmentId),
    queryFn: () => trpc.telemedicine.getAppointmentJoinLink.query({ appointmentId }),
    enabled: options.enabled && appointmentId.length > 0,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    staleTime: JOIN_LINK_SHARED_STALE_MS,
    cacheTime: 0
  })
  return { joinUrl: query.data?.joinUrl ?? null }
}

/**
 * Cancel an appointment. On success the appointments query is invalidated so the cancelled visit
 * drops out.
 */
export function useCancelAppointment(
  onOutcome?: (appointmentId: string, succeeded: boolean, cancellationAttemptId: string) => void
): CancelAppointmentResult {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ appointmentId }: { appointmentId: string; cancellationAttemptId: string }) =>
      trpc.telemedicine.cancelAppointment.mutate({ appointmentId }),
    onSuccess: (_result, { appointmentId, cancellationAttemptId }) => {
      onOutcome?.(appointmentId, true, cancellationAttemptId)
      return queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY })
    },
    // Errors surface via `isError`; onError keeps the rejected mutation observed (no unhandled reject).
    onError: (_error, { appointmentId, cancellationAttemptId }) =>
      onOutcome?.(appointmentId, false, cancellationAttemptId)
  })

  // Fire-and-forget with a success callback (not mutateAsync): a failed cancel never calls onSuccess,
  // so the dialog can't report a false "canceled", and `isError` drives the error message.
  return {
    mutate: (appointmentId, options) =>
      mutation.mutate(
        { appointmentId, cancellationAttemptId: options.cancellationAttemptId },
        { onSuccess: options.onSuccess }
      ),
    isLoading: mutation.isPending,
    isError: mutation.isError
  }
}

/**
 * Optimistically mark an appointment's intake forms complete on submit, ahead of Healthie's completion
 * webhook. Call from an intake-submit mutation's onSuccess with the intake URL's appointment id.
 *
 * Adds the id to the overlay (see FORMS_COMPLETE_OVERLAY_KEY) and refetches the appointments query, so
 * it applies even when that cache was empty or stale; reconcileFormsCompleteOverlay drops it once the
 * server confirms. Also schedules a prune at the cap so it self-corrects even on a page left open with
 * no further fetch (reconcile alone is fetch-driven) — the entry can't outlive FORMS_COMPLETE_MAX_MS.
 */
export function markAppointmentFormsComplete(queryClient: QueryClient, appointmentId: string): void {
  queryClient.setQueryData<FormsCompleteOverlayEntry[]>([FORMS_COMPLETE_OVERLAY_KEY], (entries) =>
    entries?.some((entry) => entry.id === appointmentId)
      ? entries
      : [...(entries ?? []), { id: appointmentId, since: Date.now() }]
  )
  void queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY })

  const capTimer = setTimeout(() => {
    queryClient.setQueryData<FormsCompleteOverlayEntry[]>([FORMS_COMPLETE_OVERLAY_KEY], (entries) =>
      (entries ?? []).filter((entry) => entry.id !== appointmentId)
    )
    // Refresh so we hand off to the server's current value rather than a stale pre-cap read.
    void queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY })
  }, FORMS_COMPLETE_MAX_MS)
  // A pending cap timer shouldn't keep a Node process (e.g. tests) alive; no-op in the browser.
  ;(capTimer as unknown as { unref?: () => void }).unref?.()
}
